// ─────────────────────────────────────────────────────────────────────────────
// icons — the single source of truth for the app's inline SVG glyphs (#2068).
//
// Promoted here from components/round-detail/icons.jsx: the round-action
// toolbar icons were already shared inside round-detail/, while the disclosure
// chevron had been hand-copied into nine components and the close ✕ into three.
// components/Chevron.jsx — a TENTH copy, extracted by #2066 while this work was
// in flight — is folded in here too rather than left as a second shared home.
// Every glyph is a monochrome stroke SVG (Lucide/Feather style) that inherits
// text colour via stroke="currentColor", so they theme correctly in light and
// dark. No hex colours.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Disclosure chevron ───
// Points down when `open`, left (−90°) when closed.
//
// The nine copies this replaces differed in two ways:
//   • size — 14px on wizard/dropdown controls, 12px on the compact in-card
//     disclosures. INTENTIONAL, so it stays a prop (default 14).
//   • transition duration — .2s on six copies, .15s on two. DRIFT with no
//     design intent behind it; unified on .2s here.
//
// Omit `open` entirely when rotation is driven by a CSS class instead (the
// round-detail settings header does this) — no rotation class is emitted, so
// the caller's own `--open` rule still owns `transform`.
//
// The presentation is `.disclosure-caret` in styles.css (#2076), not an inline
// object: the base carries only `flex-shrink`, and the animated/closed states
// are modifiers, so the `open === undefined` case emits exactly what it did
// before (flex-shrink and nothing else). `style` is gone with it — the one
// caller that used it (Score's notes toggle) passes `disclosure-caret--push`.
export function Chevron({ open, size = 14, className }) {
  const cls = [
    "disclosure-caret",
    open === undefined ? null : "disclosure-caret--anim",
    open === false ? "disclosure-caret--closed" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      className={cls}
    >
      <path
        d="M3 5l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Close ✕ ───
// The three copies this replaces were byte-identical but for strokeLinejoin,
// which one carried and two did not. An ✕ is two straight strokes with no
// joins, so the attribute is inert — kept for consistency with iconProps.
export function IconClose({ size = 16 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      width={size}
      height={size}
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ─── Round-action toolbar icons ───
const iconProps = {
  className: "ra-icon",
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
};

export function IconPaperclip() {
  return (
    <svg {...iconProps}>
      <path d="m21 8.5-9.19 9.19a4 4 0 0 1-5.66-5.66l8.49-8.49a2.5 2.5 0 0 1 3.54 3.54l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78" />
    </svg>
  );
}
export function IconChart() {
  return (
    <svg {...iconProps}>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" />
      <rect x="12" y="8" width="3" height="10" />
      <rect x="17" y="4" width="3" height="14" />
    </svg>
  );
}
// Excluded state: bar-chart with a diagonal slash overlay (reads as "off").
export function IconChartSlash() {
  return (
    <svg {...iconProps}>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" />
      <rect x="12" y="8" width="3" height="10" />
      <rect x="17" y="4" width="3" height="14" />
      <line x1="4" y1="4" x2="22" y2="22" />
    </svg>
  );
}
export function IconTrash() {
  return (
    <svg {...iconProps}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}
export function IconImage() {
  return (
    <svg {...iconProps}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.5-3.5a2 2 0 0 0-2.83 0L5 21" />
    </svg>
  );
}
export function IconFileText() {
  return (
    <svg {...iconProps}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="8" y1="9" x2="10" y2="9" />
    </svg>
  );
}
